export enum Suffix {
  None = '',
  JR = 'JR',
  SR = 'SR',
  II = 'II',
  TH = 'TH',
  FO = 'FO',
}

export interface DecedentInfo {
  firstName: string; // The first name of the decedent.
  middleName?: string; // The middle name of the decedent.
  lastName: string; // The last name of the decedent.
  tin: string; // The social security number of the decedent.
  zipCode: string; // The zip code of the decedent.
  dateOfDeath: string; // The date of death of the decedent.
  dateOfBirth: string; // The date of birth of the decedent.
  maritalStatus?: MaritalStatus; // The marital status of the decedent.
  suffix?: Suffix; //The suffix of the decedent
}

export enum MaritalStatus {
  DIVORCED = 'DIVORCED',
  DOMESTIC_PARTNER = 'DOMESTIC_PARTNER',
  MARRIED = 'MARRIED',
  NOT_MARRIED = 'NOT_MARRIED',
  SEPARATED = 'SEPARATED',
  SINGLE = 'SINGLE',
  UNDISCLOSED = 'UNDISCLOSED',
  UNKNOWN = 'UNKNOWN',
  WIDOWED = 'WIDOWED',
}

/*** Extracts first name, middle name, last name, and suffix from a full name string.
 *
 * @param fullName - The full name string (e.g., 'Jane A. Doe Jr', 'John Smith III', 'Dr. Mary Jane Watson Sr.')
 * @returns An object with firstName, lastName, middleName, and suffix
 *
 * @example
 * extractNameFromString('Jane A. Doe Jr')
 * // Returns: { firstName: 'Jane', middleName: 'A.', lastName: 'Doe', suffix: Suffix.JR }
 *
 * extractNameFromString('John Smith')
 * // Returns: { firstName: 'John', middleName: '', lastName: 'Smith', suffix: Suffix.None }
 */
// eslint-disable-next-line complexity, max-statements
export function extractNameFromString(
  fullName: string | undefined,
): Pick<DecedentInfo, 'firstName' | 'lastName' | 'middleName' | 'suffix'> {
  // Default return value
  const defaultResult = {
    firstName: '',
    lastName: '',
    middleName: '',
    suffix: Suffix.None,
  };

  if (!fullName || typeof fullName !== 'string') {
    return defaultResult;
  }

  // Trim and normalize whitespace
  const normalized = fullName.trim().replace(/\s+/g, ' ');

  if (!normalized) {
    return defaultResult;
  }

  // Common prefixes to remove (case insensitive)
  const prefixes = ['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'rev'];

  // Suffix mapping (case insensitive)
  const suffixMap: Record<string, Suffix> = {
    jr: Suffix.JR,
    'jr.': Suffix.JR,
    junior: Suffix.JR,
    sr: Suffix.SR,
    'sr.': Suffix.SR,
    senior: Suffix.SR,
    ii: Suffix.II,
    '2nd': Suffix.II,
    iii: Suffix.TH,
    th: Suffix.TH,
    '3rd': Suffix.TH,
    third: Suffix.TH,
    iv: Suffix.FO,
    fo: Suffix.FO,
    '4th': Suffix.FO,
    fourth: Suffix.FO,
  };

  // Split the name into parts
  let parts = normalized.split(' ');

  // Remove prefix if present
  if (parts.length > 1 && prefixes.includes(parts[0].toLowerCase().replace('.', ''))) {
    parts = parts.slice(1);
  }

  // Check for suffix in the last part
  let suffix = Suffix.None;
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1].toLowerCase();
    const matchedSuffix = suffixMap[lastPart];

    if (matchedSuffix) {
      suffix = matchedSuffix;
      parts = parts.slice(0, -1); // Remove suffix from parts
    }
  }

  // Now extract first, middle, and last names
  let firstName = '';
  let middleName = '';
  let lastName = '';

  if (parts.length === 1) {
    // Only one name part - treat as first name
    firstName = parts[0];
  } else if (parts.length === 2) {
    // Two parts - first and last
    firstName = parts[0];
    lastName = parts[1];
  } else if (parts.length >= 3) {
    // Three or more parts - first, middle(s), and last
    firstName = parts[0];
    lastName = parts[parts.length - 1];
    // Everything in between is middle name
    middleName = parts.slice(1, -1).join(' ');
  }

  return {
    firstName,
    lastName,
    middleName,
    suffix,
  };
}
